import { useState } from 'react';
import useInView from '../hooks/useInView';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection() {
  const [ref, inView] = useInView();
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus('success');
      setForm({ name: '', company: '', email: '', phone: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-white border border-black/12 px-4 py-3 text-sm text-[#0A1628] placeholder-[#9CA3AF] focus:outline-none focus:border-[#0A1628] transition-colors duration-200 rounded-sm";
  const labelClass = "block text-xs font-semibold text-[#0A1628] uppercase tracking-[0.15em] mb-2";

  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Top CTA Banner */}
        <div className="bg-[#0A1628] p-10 lg:p-14 mb-16">
          <div className="grid lg:grid-cols-2 items-center gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-6 h-px bg-[#E8500A]" />
                <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
                  Get Started
                </span>
              </div>
              <h2
                className="text-4xl sm:text-5xl font-black text-white leading-none tracking-tight"
                style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                data-testid="cta-heading"
              >
                Let's Build Your<br />Next Team.
              </h2>
            </div>
            <div className="lg:text-right">
              <p className="text-white/50 text-base leading-relaxed mb-6" style={{ fontFamily: '"Inter",sans-serif' }}>
                Tell us about your hiring challenge. We'll come back with a focused plan, not a generic pitch.
              </p>
              <div className="flex flex-col sm:flex-row lg:justify-end gap-3">
                <button
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-[#E8500A] text-white px-7 py-3 text-sm font-bold uppercase tracking-wide hover:bg-[#cc4408] transition-all duration-200 rounded-sm"
                  data-testid="cta-book-call-btn"
                >
                  Book a Call
                </button>
                <button
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="border border-white/25 text-white px-7 py-3 text-sm font-bold uppercase tracking-wide hover:border-white/60 hover:bg-white/5 transition-all duration-200 rounded-sm"
                  data-testid="cta-contact-btn"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div ref={ref} id="contact-form" className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: info */}
          <div
            style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.7s ease' }}
          >
            <h3
              className="text-3xl font-black text-[#0A1628] mb-4 leading-tight"
              style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
              data-testid="contact-form-heading"
            >
              Send Us a Message
            </h3>
            <p className="text-[#4B5563] text-sm leading-relaxed mb-8" style={{ fontFamily: '"Inter",sans-serif' }}>
              Whether you're hiring for one critical role or building an entire team — let's talk. We typically respond within one business day.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 border border-[#E8500A]/30 flex items-center justify-center mt-0.5 rounded-sm">
                  <span className="text-[#E8500A] text-xs font-bold">@</span>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide mb-0.5" style={{ fontFamily: '"Inter",sans-serif' }}>Email</div>
                  <a href="mailto:workwithus@targethr.us" className="text-sm text-[#4B5563] hover:text-[#E8500A] transition-colors" style={{ fontFamily: '"Inter",sans-serif' }} data-testid="contact-email">
                    workwithus@targethr.us
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 border border-[#E8500A]/30 flex items-center justify-center mt-0.5 rounded-sm">
                  <span className="text-[#E8500A] text-xs font-bold">TX</span>
                </div>
                <div>
                  <div className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide mb-0.5" style={{ fontFamily: '"Inter",sans-serif' }}>Headquarters</div>
                  <p className="text-sm text-[#4B5563]" style={{ fontFamily: '"Inter",sans-serif' }}>Dallas, Texas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div
            style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.7s ease 0.15s' }}
          >
            {status === 'success' ? (
              <div className="border border-[#E8500A]/20 bg-[#E8500A]/5 p-8 text-center" data-testid="contact-success">
                <div className="text-3xl font-black text-[#0A1628] mb-3" style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}>Message Sent.</div>
                <p className="text-sm text-[#4B5563]" style={{ fontFamily: '"Inter",sans-serif' }}>
                  Thank you for reaching out. We'll be in touch within one business day.
                </p>
                <button onClick={() => setStatus('idle')} className="mt-6 text-xs text-[#E8500A] font-semibold uppercase tracking-wide hover:underline">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass} htmlFor="name" style={{ fontFamily: '"Inter",sans-serif' }}>Name *</label>
                    <input id="name" name="name" type="text" required className={inputClass} placeholder="Your full name" value={form.name} onChange={handleChange} data-testid="contact-name-input" style={{ fontFamily: '"Inter",sans-serif' }} />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="company" style={{ fontFamily: '"Inter",sans-serif' }}>Company *</label>
                    <input id="company" name="company" type="text" required className={inputClass} placeholder="Company name" value={form.company} onChange={handleChange} data-testid="contact-company-input" style={{ fontFamily: '"Inter",sans-serif' }} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} htmlFor="email" style={{ fontFamily: '"Inter",sans-serif' }}>Email *</label>
                  <input id="email" name="email" type="email" required className={inputClass} placeholder="you@company.com" value={form.email} onChange={handleChange} data-testid="contact-email-input" style={{ fontFamily: '"Inter",sans-serif' }} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone" style={{ fontFamily: '"Inter",sans-serif' }}>Phone</label>
                  <input id="phone" name="phone" type="tel" className={inputClass} placeholder="+1 (optional)" value={form.phone} onChange={handleChange} data-testid="contact-phone-input" style={{ fontFamily: '"Inter",sans-serif' }} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="message" style={{ fontFamily: '"Inter",sans-serif' }}>Message *</label>
                  <textarea id="message" name="message" required rows={4} className={inputClass} placeholder="Tell us about your hiring need..." value={form.message} onChange={handleChange} data-testid="contact-message-input" style={{ fontFamily: '"Inter",sans-serif', resize: 'none' }} />
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500" data-testid="contact-error">Something went wrong. Please try again or email us directly.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-[#0A1628] text-white py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#E8500A] disabled:opacity-60 transition-all duration-200 rounded-sm"
                  data-testid="contact-submit-btn"
                  style={{ fontFamily: '"Inter",sans-serif' }}
                >
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
